import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMeeting } from '../contexts/MeetingContext';
import VodafoneLogo from './VodafoneLogo';
import AgendaChatPanel from './AgendaChatPanel';
import PDFDocumentViewer from './PDFDocumentViewer';
import {
  HomeIcon,
  ChartBarIcon,
  LinkIcon,
  DocumentTextIcon,
  FolderIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ChevronDownIcon,
  PresentationChartBarIcon,
  ChatBubbleLeftRightIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const VodafoneHeader = () => (
  <div className="flex items-center space-x-3">
    <div className="hidden lg:block">
      <h1 className="text-lg font-semibold text-charcoal-900">3GPP Delta</h1>
    </div>
  </div>
);

const navigation = [
  { name: 'Agenda Context', href: '/', icon: DocumentTextIcon },
    { name: 'Proposal Analytics', href: '/home', icon: PresentationChartBarIcon },
  { name: 'Subtopic Browser', href: '/subtopics', icon: ChartBarIcon },
  { name: 'Documents', href: '/documents', icon: FolderIcon },
  { name: 'Conclusion Analysis', href: '/cross-reference', icon: LinkIcon },
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [meetingDropdownOpen, setMeetingDropdownOpen] = useState(false);
  const [agendaDropdownOpen, setAgendaDropdownOpen] = useState(false);
  const [documentViewer, setDocumentViewer] = useState({
    isOpen: false,
    documentId: null,
    initialPage: null,
  });
  const selectedAgendaRef = useRef(null);
  const { user, logout } = useAuth();
  const { 
    selectedMeeting, 
    selectedAgenda, 
    availableMeetings, 
    availableAgendas,
    setMeeting,
    setAgenda,
    loading: meetingLoading,
    chatOpen,
    setChatOpen,
  } = useMeeting();
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-position to selected agenda when dropdown opens (instant, no animation)
  useEffect(() => {
    if (agendaDropdownOpen && selectedAgendaRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        selectedAgendaRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'center'
        });
      });
    }
  }, [agendaDropdownOpen]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleMeetingChange = (meeting) => {
    setMeeting(meeting);
    setMeetingDropdownOpen(false);
    toast.success(`Switched to ${meeting}`);
  };

  const handleAgendaChange = (agenda) => {
    setAgenda(agenda);
    setAgendaDropdownOpen(false);
    toast.success(`Switched to Agenda ${agenda}`);
  };

  const isCurrentPage = (href) => location.pathname === href;

  const handleChatCitationClick = (docId, page) => {
    setDocumentViewer({
      isOpen: true,
      documentId: docId,
      initialPage: page,
    });
  };

  const closeDocumentViewer = () => {
    setDocumentViewer({
      isOpen: false,
      documentId: null,
      initialPage: null,
    });
  };

  const handleExportMeeting = async () => {
    if (!selectedMeeting) {
      toast.error('Please select a meeting first');
      return;
    }

    try {
      toast.loading('Generating Excel file for entire meeting...');
      
      const response = await fetch(
        `${API_BASE_URL}/meetings/${encodeURIComponent(selectedMeeting)}/export-excel`
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedMeeting}_all_agendas.xlsx`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.dismiss();
      toast.success('Meeting Excel file downloaded!');
      
    } catch (error) {
      toast.dismiss();
      console.error('Excel export error:', error);
      toast.error('Failed to export meeting');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`relative z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 z-50 w-72 bg-white px-6 py-6 shadow-xl">
          <div className="flex items-center justify-between">
            <VodafoneHeader />
            <button
              type="button"
              className="rounded-md p-2 text-gray-700"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="mt-8">
            <ul className="space-y-2">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isCurrentPage(item.href)
                        ? 'bg-vodafone-50 text-vodafone-600'
                        : 'text-charcoal-700 hover:bg-gray-50 hover:text-charcoal-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 py-6 shadow-sm border-r border-gray-200">
          <VodafoneHeader />
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-2">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isCurrentPage(item.href)
                        ? 'bg-vodafone-50 text-vodafone-600 border-r-2 border-vodafone-500'
                        : 'text-charcoal-700 hover:bg-gray-50 hover:text-charcoal-900'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="h-6 w-px bg-gray-200 lg:hidden" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-3 lg:gap-x-4">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
              
              {/* Meeting Selector */}
              <div className="relative">
                <button 
                  onClick={() => setMeetingDropdownOpen(!meetingDropdownOpen)}
                  className="flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all"
                  disabled={meetingLoading}
                >
                  <span className="hidden sm:inline">Meeting:</span>
                  <span className="font-semibold text-vodafone-600">{selectedMeeting}</span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>
                {meetingDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setMeetingDropdownOpen(false)}
                    />
                    <div className="absolute left-0 z-20 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="py-1 max-h-60 overflow-y-auto">
                        {availableMeetings.map((meeting) => (
                          <button
                            key={meeting}
                            onClick={() => handleMeetingChange(meeting)}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              meeting === selectedMeeting
                                ? 'bg-vodafone-50 text-vodafone-700 font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {meeting}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Agenda Selector */}
              <div className="relative">
                <button 
                  onClick={() => setAgendaDropdownOpen(!agendaDropdownOpen)}
                  className="flex items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all"
                  disabled={meetingLoading}
                >
                  <span className="hidden sm:inline">Agenda:</span>
                  <span className="font-semibold text-vodafone-600">
                    {(() => {
                      const currentAgenda = availableAgendas.find(a => 
                        (typeof a === 'string' ? a : a.agenda_item) === selectedAgenda
                      );
                      if (typeof currentAgenda === 'object' && currentAgenda?.description) {
                        return `${selectedAgenda} - ${currentAgenda.description}`;
                      }
                      return selectedAgenda;
                    })()}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>
                {agendaDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setAgendaDropdownOpen(false)}
                    />
                    <div className="absolute left-0 z-20 mt-2 w-96 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="py-1 max-h-60 overflow-y-auto">
                        {availableAgendas.map((agenda) => {
                          const agendaItem = typeof agenda === 'string' ? agenda : agenda.agenda_item;
                          const agendaDescription = typeof agenda === 'object' && agenda.description 
                            ? agenda.description 
                            : '';
                          const isSelected = agendaItem === selectedAgenda;
                          return (
                            <button
                              key={agendaItem}
                              ref={isSelected ? selectedAgendaRef : null}
                              onClick={() => handleAgendaChange(agendaItem)}
                              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'bg-vodafone-50 text-vodafone-700 font-semibold'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {agendaDescription ? (
                                <div>
                                  <span className="font-semibold">{agendaItem}</span>
                                  <span className="text-gray-600"> - {agendaDescription}</span>
                                </div>
                              ) : (
                                agendaItem
                              )}
                            </button>
                          );
                        })}
                        {availableAgendas.length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center italic">
                            No agendas with proposals available
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Chat with AI button */}
              <button
                onClick={() => setChatOpen(!chatOpen)}
                className="flex items-center gap-x-2 rounded-md bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all"
                title="Chat with AI Assistant"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Chat with AI</span>
              </button>
              
              {/* Export Meeting to Excel button */}
              <button
                onClick={handleExportMeeting}
                disabled={!selectedMeeting}
                className="flex items-center gap-x-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white shadow-sm transition-all"
                title="Export entire meeting to Excel"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Export Meeting</span>
              </button>
            </div>

            <div className="ml-auto flex items-center gap-x-4 lg:gap-x-6">
              {/* User menu */}
              <div className="flex items-center gap-x-3">
                <div className="flex items-center gap-x-2 text-sm">
                  <UserIcon className="h-5 w-5 text-charcoal-500" />
                  <span className="text-charcoal-700 font-medium">{user?.username}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-x-2 text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span className="hidden sm:block">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
      
      {/* Chat Panel - persists across all pages */}
      <AgendaChatPanel onCitationClick={handleChatCitationClick} />
      
      {/* Document Viewer */}
      {documentViewer.isOpen && (
        <PDFDocumentViewer
          documentId={documentViewer.documentId}
          isOpen={documentViewer.isOpen}
          onClose={closeDocumentViewer}
          initialPage={documentViewer.initialPage}
        />
      )}
    </div>
  );
};

export default Layout; 